import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Spinner, XCircle } from "@phosphor-icons/react";
import { getPaymentStatus } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const attempts = useRef(0);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          setTimeout(() => navigate("/welcome", { replace: true }), 800);
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_POLLS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="mx-auto flex min-h-[80vh] max-w-sm flex-col items-center justify-center px-4 py-16 text-center">
      {state === "polling" && (
        <>
          <Spinner size={32} className="mb-4 animate-spin text-eb-accent" />
          <h1 className="font-heading text-xl uppercase text-white">Confirming your payment&hellip;</h1>
          <p className="mt-2 text-sm text-zinc-500">This usually takes a few seconds.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <h1 className="font-heading text-xl uppercase text-white">Payment confirmed!</h1>
          <p className="mt-2 text-sm text-zinc-500">Taking you to EdgeBall&hellip;</p>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <XCircle size={32} className="mb-4 text-red-400" />
          <h1 className="font-heading text-xl uppercase text-white">Still confirming</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Your payment may still be processing. Refresh in a moment, or return to{" "}
            <Link to="/subscribe" data-testid="payment-success-retry-link" className="text-eb-accent hover:underline">
              subscribe
            </Link>
            .
          </p>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
